import { QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { initHttpClients, initPublicHttpClients, resetClients } from '@/shared/api/client';

import { UnleashProvider, AuthSessionProviders } from '@/features/session-providers';
import { ErrorBoundary } from '@/shared/components/error-boundaries/ErrorBoundary';
import { queryClient } from '@/shared/lib/query-client';
import { useConfig } from '@/shared/utils/config/config-context';
import { initializeFirebase } from '@/shared/utils/firebase/firebase';
import { initializePdfJs } from '@/shared/utils/pdfjs/pdfjs';
import { UnauthenticatedFallback } from '@/features/session-providers';
import { ProtectedRoute } from './ProtectedRoute';
import { useAuth } from '@/shared/components/auth/AuthProvider';
import { isAuthenticatedRamConfig } from '@/shared/env/runtime-config';
import { RouteLoader } from '@/shared/components/ui/route-loader';

import { BootstrapErrorScreen } from './BootstrapErrorScreen';
import { ConfigProvider } from './ConfigProvider';
import { router } from '../router';

function dismissSplashScreen() {
    const splashElement = document.getElementById('splash-screen');
    if (splashElement) {
        splashElement.classList.add('fade-out');
        setTimeout(() => {
            splashElement.remove();
            document.body.style.overflow = 'auto';
        }, 10); // Match CSS transition duration
    }
}

function AppAfterConfigReady() {
    const { config, loading, error } = useConfig();
    const [isRuntimeReady, setIsRuntimeReady] = useState(false);

    // Initialize Firebase, HTTP clients, and PDF.js together once config is available.
    useEffect(() => {
        if (!config) {
            setIsRuntimeReady(false);
            return;
        }

        initializeFirebase({
            FBASE_API_KEY: config.FBASE_API_KEY,
            FBASE_AUTH_DOMAIN: config.FBASE_AUTH_DOMAIN,
            FBASE_PROJECT_ID: config.FBASE_PROJECT_ID,
        });
        initPublicHttpClients(config);
        initializePdfJs();
        setIsRuntimeReady(true);
    }, [config]);

    // Safety timeout: dismiss splash after 3 seconds if auth never completes
    useEffect(() => {
        const maxTimer = setTimeout(() => {
            dismissSplashScreen();
        }, 3000);
        return () => clearTimeout(maxTimer);
    }, []);

    if (loading) {
        return null; // Splash screen is visible from HTML
    }

    if (error || !config) {
        dismissSplashScreen();
        return (
            <BootstrapErrorScreen
                variant="config"
                onReload={() => {
                    window.location.reload();
                }}
            />
        );
    }

    if (!isRuntimeReady) {
        return null;
    }

    return (
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <AuthSessionProviders>
                    <AuthenticatedAppShell />
                </AuthSessionProviders>
            </QueryClientProvider>
        </ErrorBoundary>
    );
}

function AuthenticatedAppShell() {
    const { config, error, isLoadingAuthenticatedConfig, loadAuthenticatedConfig, loadPublicConfig } = useConfig();
    const { user, loading, login } = useAuth();

    // Runs after auth resolves with a signed-in user and public config, then upgrades the app to authenticated config once.
    useEffect(() => {
        if (loading || !user || !config || isAuthenticatedRamConfig(config) || isLoadingAuthenticatedConfig) {
            return;
        }

        void loadAuthenticatedConfig();
    }, [config, isLoadingAuthenticatedConfig, loadAuthenticatedConfig, loading, user]);

    // Runs after logout while authenticated config is still loaded, restoring the public config and resetting API clients.
    useEffect(() => {
        if (loading || user || !config || !isAuthenticatedRamConfig(config)) {
            return;
        }

        resetClients();
        void loadPublicConfig();
    }, [config, loadPublicConfig, loading, user]);

    // Runs once auth/config have settled enough for the app shell to render, then removes the HTML splash screen.
    useEffect(() => {
        if (!loading && (!user || (config && isAuthenticatedRamConfig(config)))) {
            dismissSplashScreen();
        }
    }, [config, loading, user]);

    if (error) {
        dismissSplashScreen();
        return (
            <BootstrapErrorScreen
                variant="auth"
                onReload={() => {
                    window.location.reload();
                }}
                onRelogin={login}
            />
        );
    }

    if (loading || isLoadingAuthenticatedConfig || (user && config && !isAuthenticatedRamConfig(config))) {
        return null;
    }

    // Initialize Propx/Basecamp **before** RouterProvider mounts. Child useEffect (e.g. React Query) runs before parent
    // useEffect, so relying only on useEffect(initHttpClients) races: queries can run while `clients.propx` is still unset.
    if (config && isAuthenticatedRamConfig(config)) {
        initHttpClients(config);
    }

    return (
        <UnleashProvider>
            <ProtectedRoute unauthenticatedFallback={<UnauthenticatedFallback />}>
                {config && isAuthenticatedRamConfig(config) ? (
                    <Suspense fallback={<RouteLoader />}>
                        <RouterProvider router={router} />
                    </Suspense>
                ) : null}
            </ProtectedRoute>
        </UnleashProvider>
    );
}

/**
 * Fetches `/config`, initializes Firebase + HTTP clients, then mounts the routed app.
 */
export function AppBootstrap() {
    return (
        <ConfigProvider>
            <AppAfterConfigReady />
        </ConfigProvider>
    );
}
